const API_BASE = process.env.REACT_APP_API_BASE || "/v1";
const API_KEY = process.env.REACT_APP_API_KEY;

function getAuthToken() {
  return localStorage.getItem("token") || localStorage.getItem("auth_token");
}

function appendApiKey(url) {
  if (!API_KEY) return url;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}api_key=${encodeURIComponent(API_KEY)}`;
}

async function request(path, options = {}) {
  const token = getAuthToken();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};
  const url = appendApiKey(`${API_BASE}${path}`);
  const response = await fetch(url, {
    headers: { "Content-Type": "application/json", ...authHeader },
    ...options,
  });
  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || "Request failed");
  }
  return response.status === 204 ? null : response.json();
}

export const api = {
  listCatalogVendors: async ({
    categories,
    page,
    limit,
    search,
    status,
  } = {}) => {
    const params = new URLSearchParams();
    if (categories && categories.length) {
      params.append(
        "categories",
        Array.isArray(categories) ? categories.join(",") : categories,
      );
    }
    if (search) params.append("search", search);
    if (status) params.append("status", status);
    if (page) params.append("page", String(page));
    if (limit) params.append("limit", String(limit));
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return request(`/catalog/vendors${suffix}`);
  },
  createCatalogVendor: (payload) =>
    request("/catalog/vendors", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  updateCatalogVendor: (id, payload) =>
    request(`/catalog/vendors/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  deleteCatalogVendor: (id) =>
    request(`/catalog/vendors/${id}`, { method: "DELETE" }),
  // Menu-category display order for the marketplace vendor screen. Categories
  // are free-text subCategory labels, so the order is a list of labels.
  // Returns { vendorId, categoryOrder, categories } — `categories` is the live
  // list already resolved through the saved order.
  getCatalogVendorCategoryOrder: (id) =>
    request(`/catalog/vendors/${id}/category-order`),
  // An empty categoryOrder clears it and restores the alphabetical default.
  updateCatalogVendorCategoryOrder: (id, categoryOrder) =>
    request(`/catalog/vendors/${id}/category-order`, {
      method: "PUT",
      body: JSON.stringify({ categoryOrder }),
    }),
  listCatalogVendorAttachments: (id) =>
    request(`/catalog/vendors/${id}/attachments`),
  uploadCatalogVendorAttachment: async ({ id, file }) => {
    const token = getAuthToken();
    const formData = new FormData();
    formData.append("file", file);
    const uploadUrl = appendApiKey(
      `${API_BASE}/catalog/vendors/${id}/attachments`,
    );
    const response = await fetch(uploadUrl, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || "Upload failed");
    }
    return response.json();
  },
  deleteCatalogVendorAttachment: (id, attachmentId) =>
    request(`/catalog/vendors/${id}/attachments/${attachmentId}`, {
      method: "DELETE",
    }),

  listCatalogListings: ({
    page,
    limit,
    search,
    category,
    status,
    type,
    subCategory,
    tag,
    vendorId,
  } = {}) => {
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    if (category) params.append("category", category);
    if (status) params.append("status", status);
    if (type) params.append("type", type);
    if (subCategory) params.append("subCategory", subCategory);
    if (tag) params.append("tag", tag);
    if (vendorId) params.append("vendorId", vendorId);
    if (page) params.append("page", String(page));
    if (limit) params.append("limit", String(limit));
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return request(`/catalog/listings${suffix}`);
  },
  createCatalogListing: (payload) =>
    request("/catalog/listings", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  updateCatalogListing: (id, payload) =>
    request(`/catalog/listings/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  deleteCatalogListing: (id) =>
    request(`/catalog/listings/${id}`, { method: "DELETE" }),

  listCatalogVendorListings: ({ page, limit, search } = {}) => {
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    if (page) params.append("page", String(page));
    if (limit) params.append("limit", String(limit));
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return request(`/catalog/vendor-listings${suffix}`);
  },
  createCatalogVendorListing: (payload) =>
    request("/catalog/vendor-listings", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  updateCatalogVendorListing: (id, payload) =>
    request(`/catalog/vendor-listings/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  reorderCatalogVendorListings: (payload) =>
    request(`/catalog/vendor-listings/reorder`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  deleteCatalogVendorListing: (id) =>
    request(`/catalog/vendor-listings/${id}`, { method: "DELETE" }),
  uploadCatalogPhotoFile: async ({ file, photoType }) => {
    const token = getAuthToken();
    const formData = new FormData();
    formData.append("file", file);
    formData.append("photoType", photoType);
    formData.append("entity", "catalog_listing");
    const uploadUrl = appendApiKey(`${API_BASE}/uploads/upload`);
    const response = await fetch(uploadUrl, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || "Upload failed");
    }
    return response.json();
  },
  importCatalogPhotoUrl: async ({ sourceUrl, photoType }) => {
    return request("/catalog/uploads/import-url", {
      method: "POST",
      body: JSON.stringify({ sourceUrl, photoType, entity: "catalog_listing" }),
    });
  },
  loadCatalogVariantNames: ({ query } = {}) => {
    const params = new URLSearchParams();
    if (query) params.append("query", query);
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return request(`/catalog/listings/variant-names${suffix}`);
  },
  searchLocations: async ({
    latitude,
    longitude,
    query,
    categories,
    radiusMeters,
    limit,
    category,
  }) => {
    const params = new URLSearchParams();
    if (latitude !== undefined && latitude !== "")
      params.append("latitude", String(latitude));
    if (longitude !== undefined && longitude !== "")
      params.append("longitude", String(longitude));
    if (query) params.append("query", query);
    if (categories) params.append("categories", categories);
    if (category) params.append("category", category);
    if (radiusMeters) params.append("radiusMeters", String(radiusMeters));
    if (limit) params.append("limit", String(limit));
    return request(`/locations/search?${params.toString()}`);
  },
  googleSignIn: async ({ idToken, userType = null } = {}) =>
    request("/auth/google", {
      method: "POST",
      body: JSON.stringify({ idToken, userType }),
    }),
  firebasePhoneSignIn: async ({ idToken, userType = "merchant" } = {}) =>
    request("/auth/firebase-phone", {
      method: "POST",
      body: JSON.stringify({
        idToken,
        ...(userType ? { userType } : {}),
        platform: "web",
      }),
    }),
  listMerchantTeam: () => request(`/merchants/me/team`),
  addMerchantTeammate: (email) =>
    request(`/merchants/me/team`, {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  removeMerchantTeammate: (userId) =>
    request(`/merchants/me/team/${userId}`, { method: "DELETE" }),
};
